import React, { useState } from "react";
import {
    Card, CardHeader, CardBody, Badge, Button, Collapse, ListGroup, ListGroupItem
} from "reactstrap";

function money(x) {
    if (x === null || x === undefined || isNaN(x)) return "—";
    const n = Number(x);
    return `$${n.toFixed(2)}`;
}

function line(label, value) {
    if (!value && value !== 0) return null;
    return (
        <div className="d-flex justify-content-between align-items-center small mb-1">
            <span className="text-muted">{label}</span>
            <span className="fw-semibold">{value}</span>
        </div>
    );
}

function renderDrugHeader(title, form, strength, qty) {
    return (
        <>
            <h6 className="mb-1">{title || "Drug"}</h6>
            <div className="text-muted small">
                {[form, strength, qty ? `Qty: ${qty}` : null].filter(Boolean).join(" | ")}
            </div>
        </>
    );
}

const SavedDrugsList = ({ homeRetailDrug, savedMeds }) => {
    const [showMoreLocations, setShowMoreLocations] = useState(false);

    const hasData = Boolean(homeRetailDrug) || (Array.isArray(savedMeds) && savedMeds.length > 0);

    // Pull parsed bits for homeRetailDrug gracefully
    const hr = homeRetailDrug || null;
    const hrTitle = hr?.name || hr?.ln;
    const hrDescParts = (hr?.description || "").split("|").map(s => s?.trim());
    const hrForm = hr?.form || hrDescParts[0];       // e.g., "Tablet"
    const hrStrength = hr?.strength || hrDescParts[1]; // e.g., "800 MG"
    const hrQty = hr?.qty || Number(hrDescParts?.[2]?.replace(/[^0-9]/g, "")) || null;

    const moreLocations = Array.isArray(hr?.moreDrugLocations) ? hr.moreDrugLocations : [];

    return (
        <Card className="border">
            <CardHeader style={{ background: "var(--thead-bg)" }}>
                <h5 className="mb-0">Saved Drugs</h5>
            </CardHeader>

            <CardBody>
                {!hasData ? (
                    <div className="text-center py-4">
                        <i className="ri-heart-line text-muted mb-3" style={{ fontSize: "2rem" }} />
                        <p className="text-muted mb-0">No saved drugs yet</p>
                        <small className="text-muted">User's saved medications will appear here</small>
                    </div>
                ) : (
                    <>
                        {/* Highlight the home retail drug first */}
                        {hr && (
                            <>
                                <h6 className="mb-3">Home Retail Drug</h6>

                                <Card className="mb-3 border">
                                    <CardBody className="py-3">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                {renderDrugHeader(hrTitle, hrForm, hrStrength, hrQty)}
                                                <div className="mt-2">
                                                    {hr?.pharmacyName && <Badge color="primary" className="me-2">{hr.pharmacyName}</Badge>}
                                                    {hr?.distance != null && <Badge color="secondary" className="me-2">{hr.distance} mi away</Badge>}
                                                    {hr?.city && hr?.state && <Badge color="light" className="text-muted">{hr.city}, {hr.state}</Badge>}
                                                </div>
                                            </div>

                                            {/* Price pill */}
                                            <div className="text-end">
                                                <div className="fw-bold fs-6">{money(hr?.price ?? hr?.macPrice ?? hr?.ucPrice)}</div>
                                                <div className="text-muted small">Your Price</div>
                                            </div>
                                        </div>

                                        <hr className="my-3" />

                                        {/* Key details */}
                                        {line("Price (MAC/UC)", `${money(hr?.macPrice)} / ${money(hr?.ucPrice)}`)}
                                        {line("Pharmacy Phone", hr?.phone)}
                                        {line("Address", hr?.address && `${hr.address}${hr.zip ? `, ${hr.zip}` : ""}`)}
                                        {line("NDC", hr?.ndc)}
                                        {line("Hours", hr?.hoursOfOperation)}

                                        {/* More locations */}
                                        {moreLocations.length > 0 && (
                                            <>
                                                <div className="mt-2">
                                                    <a
                                                        className="p-0 cursor-pointer"
                                                        onClick={() => setShowMoreLocations((s) => !s)}
                                                    >
                                                        {showMoreLocations ? "Hide" : "Show"} more locations ({moreLocations.length})
                                                        <i className={`ri-arrow-${showMoreLocations ? "up" : "down"}-s-line ms-1`} />
                                                    </a>
                                                </div>

                                                <Collapse isOpen={showMoreLocations} className="mt-2">
                                                    <ListGroup>
                                                        {moreLocations.map((loc, idx) => (
                                                            <ListGroupItem key={idx} className="d-flex justify-content-between align-items-start">
                                                                <div>
                                                                    <div className="fw-semibold">{loc?.pharmacyName || "Pharmacy"}</div>
                                                                    <div className="small text-muted">
                                                                        {[
                                                                            loc?.city && loc?.state ? `${loc.city}, ${loc.state}` : null,
                                                                            loc?.address || null,
                                                                            loc?.zip || null
                                                                        ].filter(Boolean).join(" • ")}
                                                                    </div>
                                                                    <div className="small text-muted">
                                                                        {loc?.hoursOfOperation}
                                                                    </div>
                                                                </div>
                                                                <div className="text-end">
                                                                    <div className="fw-semibold">{money(loc?.price ?? loc?.macPrice ?? loc?.ucPrice)}</div>
                                                                    {loc?.distance != null && (
                                                                        <div className="small text-muted">{loc.distance} mi</div>
                                                                    )}
                                                                </div>
                                                            </ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                </Collapse>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </>

                        )}

                        {/* Saved meds (no pharmacy/price here, but show drug basics & qty) */}
                        {Array.isArray(savedMeds) && savedMeds.length > 0 && (
                            <>
                                <h6 className="mt-4 mb-3">Saved Medications</h6>
                                {savedMeds.map((med, idx) => {
                                    const d = med?.data || {};
                                    const title = d.drugName || d.brandDrugName || med?.query || "Drug";
                                    const form = d.form;
                                    const strength = d.strength;
                                    const qty = d.qty;

                                    return (
                                        <Card className="mb-2 border" key={idx}>
                                            <CardBody className="py-3">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        {renderDrugHeader(title, form, strength, qty)}
                                                        <div className="mt-2 small text-muted">
                                                            {/* Show the query key for debugging/context, optional */}
                                                            {med?.key && <span>Query: <code>{med.key}</code></span>}
                                                        </div>
                                                    </div>
                                                    {/* <div className="text-end">
                                                        <div className="small text-muted">Price</div>
                                                        <div className="fw-semibold">—</div>
                                                    </div> */}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default SavedDrugsList;